import React, { useEffect, useState } from 'react';
import { 
  DollarSign, 
  Package, 
  TrendingUp, 
  Eye, 
  Heart, 
  ShoppingBag,
  ArrowLeft,
  AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getSellerAnalytics } from '../../services/sellerAnalyticsService';
import { getSellerOrders } from '../../services/orderService';
import { SellerAnalytics, Order } from '../../types';
import { motion } from 'framer-motion';

const SellerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState<SellerAnalytics | null>(null);
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [analyticsData, ordersData] = await Promise.all([
        getSellerAnalytics(user.id),
        getSellerOrders(user.id)
      ]);
      setAnalytics(analyticsData);
      setRecentOrders(ordersData.slice(0, 5));
    } catch (error) {
      console.error('Error loading seller data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const statCards = [
    {
      icon: DollarSign,
      label: 'Total Revenue',
      value: `₹${analytics?.totalRevenue.toLocaleString()}`,
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-50'
    },
    {
      icon: ShoppingBag,
      label: 'Total Sales',
      value: analytics?.totalSales || 0,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      icon: AlertCircle,
      label: 'Pending Orders',
      value: analytics?.pendingOrders || 0,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      icon: Package,
      label: 'Completed',
      value: analytics?.completedOrders || 0,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      icon: Eye,
      label: 'Total Views',
      value: analytics?.totalViews || 0,
      color: 'from-cyan-500 to-cyan-600',
      bgColor: 'bg-cyan-50'
    },
    {
      icon: Heart,
      label: 'Total Likes',
      value: analytics?.totalLikes || 0,
      color: 'from-pink-500 to-pink-600',
      bgColor: 'bg-pink-50'
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-purple-600 text-white p-6 shadow-xl">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/profile')}
          className="p-2 hover:bg-white/20 rounded-full mb-4 inline-flex"
        >
          <ArrowLeft size={24} />
        </motion.button>
        
        <h1 className="text-3xl font-bold mb-2">Seller Dashboard</h1>
        <p className="opacity-90">Manage your sales and orders</p>
      </div>

      <div className="p-4">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`${stat.bgColor} rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all`}
            >
              <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mb-3`}>
                <stat.icon className="text-white" size={24} />
              </div>
              <p className="text-2xl font-bold text-gray-800 mb-1">{stat.value}</p>
              <p className="text-sm text-gray-600">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Conversion Rate */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-2xl p-6 shadow-lg mb-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="text-primary" size={24} />
            <h3 className="font-bold text-gray-800 text-lg">Performance Metrics</h3>
          </div>
          
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Conversion Rate</span>
              <span className="font-bold text-primary">{analytics?.conversionRate}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${analytics?.conversionRate}%` }}
                transition={{ duration: 1, delay: 0.7 }}
                className="bg-gradient-to-r from-primary to-purple-600 h-full rounded-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-xl">
              <p className="text-2xl font-bold text-blue-600">{analytics?.totalArtworks}</p>
              <p className="text-xs text-gray-600">Total Artworks</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-xl">
              <p className="text-2xl font-bold text-green-600">
                ₹{((analytics?.totalRevenue || 0) / (analytics?.completedOrders || 1)).toFixed(0)}
              </p>
              <p className="text-xs text-gray-600">Avg Order Value</p>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/seller/orders')}
            className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
            <AlertCircle size={32} className="mb-2" />
            <p className="text-2xl font-bold">{analytics?.pendingOrders}</p>
            <p className="text-sm opacity-90">Pending Orders</p>
          </motion.button>

          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/post')}
            className="bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
            <Package size={32} className="mb-2" />
            <p className="text-lg font-bold">Post Artwork</p>
            <p className="text-sm opacity-90">Add new item</p>
              </motion.button>
    </div>

    {/* Recent Orders */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.8 }}
      className="bg-white rounded-2xl p-6 shadow-lg"
    >
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-bold text-gray-800 text-lg">Recent Orders</h3>
        <button
          onClick={() => navigate('/seller/orders')}
          className="text-primary text-sm font-medium hover:underline"
        >
          View All
        </button>
      </div>

      {recentOrders.length === 0 ? (
        <p className="text-center text-gray-500 py-4">No orders yet</p>
      ) : (
        <div className="space-y-3">
          {recentOrders.map((order) => (
            <div
              key={order.id}
              onClick={() => navigate(`/seller/orders/${order.id}`)}
              className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 cursor-pointer transition-all"
            >
              <img
                src={order.artworkImage}
                alt={order.artworkTitle}
                className="w-12 h-12 object-cover rounded-lg"
              />
              <div className="flex-1">
                <p className="font-semibold text-gray-800 text-sm">{order.artworkTitle}</p>
                <p className="text-xs text-gray-500">{order.buyerName}</p>
              </div>
              <span className="text-primary font-bold text-sm">
                ₹{order.totalAmount.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  </div>
</div>
);
};
export default SellerDashboard;